import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useUser } from '@auth_module/hooks/useUser';
import { fetchProductPrices, fetchProducts } from '@account_module/services';
import { SUBSCRIPTION_QUERY_KEY } from '@account_module/constants/subscriptionKeys';
import { IPriceResponse, ISubscription } from '@app/types/Billing';

export const useSubscriptions = () => {
  const { user } = useUser();

  const {
    data: products,
    isLoading: isProductsLoading,
    error,
    isFetching: isProductsFetching,
  } = useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY.PRODUCTS],
    queryFn: fetchProducts,
    staleTime: 60 * 60 * 1000, // 1h
    enabled: !!user,
  });

  const {
    data: prices,
    isLoading: isPricesLoading,
    isFetching: isPricesFetching,
  } = useQuery({
    queryKey: [SUBSCRIPTION_QUERY_KEY.PRICES],
    queryFn: fetchProductPrices,
    staleTime: 60 * 60 * 1000, // 1h
    enabled: !!user,
  });

  const subscriptions = useMemo(() => {
    if (!products || !prices || isProductsLoading || isPricesLoading) return [];

    return (
      products
        .map((subscription) => {
          const billing_info = prices.find(
            (price: IPriceResponse) => price.product_id === subscription.id
          );

          if (billing_info) {
            return { ...subscription, billing_info };
          }
        })
        // Return only products with price information
        .filter((item) => !!item?.billing_info) as ISubscription[]
    );
  }, [products, prices, isProductsLoading, isPricesLoading]);

  return useMemo(
    () => ({
      data: subscriptions,
      isLoading: isProductsLoading || isPricesLoading,
      error,
      isProductsFetching,
      isPricesFetching,
    }),
    [subscriptions, isProductsLoading, isPricesLoading, isProductsFetching, isPricesFetching]
  );
};
